const pad = (value: number): string => String(value).padStart(2, "0")

/**
 * A playhead position as the player draws it — `19:39`, or `1:19:39` once the
 * video runs past an hour. Matches the TV app's `secondsToTime`, and unlike
 * `formatDuration` it always returns a string: a player with no clock is worse
 * than a player showing `0:00`.
 */
export const formatTimecode = (seconds: number): string => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const remainder = safe % 60

  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(remainder)}` : `${minutes}:${pad(remainder)}`
}

/** The `19:39/25:44` label from the OTT frame. */
export const formatTimeRange = (currentSeconds: number, durationSeconds: number): string =>
  `${formatTimecode(currentSeconds)}/${formatTimecode(durationSeconds)}`

/**
 * A spoken position for assistive tech. The slider's numeric value is the raw
 * second count, which a screen reader would otherwise read as "1179".
 */
export const describeProgress = (currentSeconds: number, durationSeconds: number): string =>
  `${formatTimecode(currentSeconds)} of ${formatTimecode(durationSeconds)}`

/** Keeps a seek target inside the video, so a drag past either end still lands. */
export const clampToDuration = (seconds: number, durationSeconds: number): number => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return 0
  }

  return durationSeconds > 0 ? Math.min(seconds, durationSeconds) : seconds
}
